import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ambulance, Phone, Loader2, HeartPulse, Activity, Building2, Baby, Accessibility,
  Truck, Snowflake, MapPin, ChevronRight, ShieldCheck,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { emergencyApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const SERVICES = {
  emergency: [
    { id: "bls", name: "Basic Life Support (BLS)", icon: Ambulance, priority: "HIGH", responseTime: "8–15 min", desc: "EMT with basic equipment, oxygen & first aid" },
    { id: "als", name: "Advanced Life Support (ALS)", icon: HeartPulse, priority: "CRITICAL", responseTime: "10–20 min", desc: "Paramedic with cardiac care, ventilator & IV meds" },
  ],
  non_emergency: [
    { id: "patient_transfer_same_city", name: "Same City Transfer", icon: Truck, priority: "MEDIUM", price: 1500, desc: "Scheduled transfer within the city" },
    { id: "patient_transfer_intercity", name: "Intercity Transfer", icon: Truck, priority: "MEDIUM", price: 5000, desc: "Long-distance transfer with medical attendant" },
    { id: "ambulance", name: "Standard Ambulance", icon: Ambulance, priority: "MEDIUM", price: 2000, desc: "General ambulance with basic attendant" },
    { id: "neonatal_transport", name: "Neonatal Transport", icon: Baby, priority: "HIGH", price: 8000, desc: "Incubator ambulance for newborns" },
    { id: "wheelchair_transport", name: "Wheelchair Transport", icon: Accessibility, priority: "LOW", price: 1200, desc: "Wheelchair-accessible vehicle" },
  ],
  mortuary: [
    { id: "dead_body_small", name: "Small Vehicle", icon: Truck, priority: "LOW", price: 3000, desc: "Compact van, short distance" },
    { id: "dead_body_medium_no_ac", name: "Medium Vehicle (Non-AC)", icon: Truck, priority: "LOW", price: 4500, desc: "Medium van without AC" },
    { id: "dead_body_big_no_ac", name: "Large Vehicle (Non-AC)", icon: Truck, priority: "LOW", price: 6000, desc: "Large vehicle, inter-city" },
    { id: "dead_body_medium_ac", name: "Medium Vehicle (AC)", icon: Snowflake, priority: "LOW", price: 7500, desc: "Temperature-controlled medium van" },
    { id: "dead_body_big_freezer", name: "Large Freezer Vehicle", icon: Snowflake, priority: "LOW", price: 12000, desc: "Full freezer van, long distance" },
  ],
};

const CATEGORIES = [
  { key: "emergency", label: "Emergency", color: "#DC2626" },
  { key: "non_emergency", label: "Non-Emergency", color: "#2563EB" },
  { key: "mortuary", label: "Mortuary", color: "#607D8B" },
];

const REASONS = ["Chest Pain & Breathlessness", "Accident", "Pregnancy", "Fire", "Unconscious", "Severe Injury", "Other"];
const HIGH_PRIORITY = ["Chest Pain & Breathlessness", "Accident", "Pregnancy", "Fire"];

function getCoords() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  });
}

export default function AmbulancePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [cat, setCat] = useState("emergency");
  const [nearest, setNearest] = useState(null);

  // Emergency SOS dialog
  const [sosService, setSosService] = useState(null);
  const [reason, setReason] = useState("");
  const [dispatching, setDispatching] = useState(false);

  // Booking dialog (non-emergency / mortuary)
  const [bookSvc, setBookSvc] = useState(null);
  const [bookingFor, setBookingFor] = useState("self");
  const [rName, setRName] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const c = await getCoords();
      if (c) {
        try {
          const res = await emergencyApi.nearestHospital(c.latitude, c.longitude);
          if (res.data?.success) setNearest(res.data);
        } catch (e) { /* ignore */ }
      }
    })();
  }, []);

  const etaFor = (svc) => {
    if (!nearest?.distanceKm) return svc.responseTime || "—";
    const extra = svc.id === "als" ? 2 : 0;
    const min = Math.round(3 + (nearest.distanceKm / 40) * 60 + extra);
    return `${min}–${Math.round(min * 1.5)} min`;
  };

  const goToCase = (res) => {
    const caseId = res.data?.caseId || res.data?.case?.id || res.data?.case?._id || res.data?.id;
    if (caseId) navigate(`/case-status/${caseId}`);
    else toast.success("Request placed. Help is being arranged.");
  };

  const confirmSOS = async () => {
    setDispatching(true);
    const location = await getCoords();
    if (!location) {
      setDispatching(false);
      toast.error("We need your location to dispatch help. Enable location access or call 112.");
      return;
    }
    try {
      const priority = HIGH_PRIORITY.includes(reason) ? "CRITICAL" : "HIGH";
      const res = await emergencyApi.create({
        customerId: user?._id,
        customerPhone: user?.phone || "",
        type: "emergency_ambulance",
        location,
        priority,
        reason: `🚨 ${reason || "Emergency"} - ${sosService.name}`,
      });
      setSosService(null);
      setReason("");
      goToCase(res);
    } catch (e) {
      toast.error("Failed to dispatch. Please call 112 directly.");
    } finally {
      setDispatching(false);
    }
  };

  const submitBooking = async () => {
    if (bookingFor === "other" && (!rName.trim() || rPhone.replace(/\D/g, "").length !== 10)) {
      return toast.error("Enter the recipient's name and 10-digit phone");
    }
    if (!pickup.trim()) return toast.error("Enter a pickup address");
    setSubmitting(true);
    const location = (await getCoords()) || { latitude: null, longitude: null };
    try {
      const res = await emergencyApi.create({
        customerId: user?._id,
        customerPhone: user?.phone || "",
        type: bookSvc.id,
        location,
        priority: bookSvc.priority,
        reason: `${bookSvc.name} - ${bookSvc.desc}`,
        servicePrice: bookSvc.price,
        pickupAddress: pickup.trim(),
        dropoffAddress: dropoff.trim() || null,
        notes: notes.trim() || null,
        recipientInfo: bookingFor === "other" ? { name: rName.trim(), phone: rPhone.replace(/\D/g, ""), address: pickup.trim() } : null,
      });
      toast.success(`${bookSvc.name} booked. Fee ₹${bookSvc.price.toLocaleString("en-IN")} — collected after service.`);
      setBookSvc(null);
      setRName(""); setRPhone(""); setPickup(""); setDropoff(""); setNotes(""); setBookingFor("self");
      goToCase(res);
    } catch (e) {
      toast.error("Failed to book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openService = (svc) => {
    if (cat === "emergency") { setSosService(svc); setReason(""); }
    else { setBookSvc(svc); setBookingFor("self"); }
  };

  return (
    <div className="max-w-3xl" data-testid="ambulance-page">
      <PageHeader title="Ambulance Services" subtitle="Emergency, transfer & mortuary transport" icon={Ambulance} color="#DC2626" />

      <a href="tel:108" data-testid="ambulance-call-108" className="flex items-center gap-3 rounded-2xl bg-destructive text-destructive-foreground px-5 py-4 mb-4 hover:opacity-95 transition-opacity">
        <Phone className="h-5 w-5" />
        <div className="flex-1"><p className="font-bold">Call 108 / 112 now</p><p className="text-xs opacity-90">For immediate life-threatening emergencies</p></div>
      </a>

      {nearest?.hospital && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <MapPin className="h-4 w-4 text-primary" /> Nearest hospital: <span className="font-medium text-foreground">{nearest.hospital.name}</span> ({nearest.hospital.distance})
        </div>
      )}

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`ambulance-cat-${c.key}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${cat === c.key ? "text-white border-transparent" : "bg-card text-muted-foreground border-border"}`}
            style={cat === c.key ? { backgroundColor: c.color } : {}}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="space-y-2.5">
        {SERVICES[cat].map((svc) => {
          const SIcon = svc.icon;
          const catColor = CATEGORIES.find((c) => c.key === cat).color;
          return (
            <Card key={svc.id} className="rounded-2xl p-4 border-border flex items-center gap-3" data-testid={`ambulance-service-${svc.id}`}>
              <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catColor + "1A" }}>
                <SIcon className="h-5 w-5" style={{ color: catColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{svc.name}</p>
                <p className="text-xs text-muted-foreground">{svc.desc}</p>
                <p className="text-xs mt-1 font-medium" style={{ color: catColor }}>
                  {cat === "emergency" ? `ETA ${etaFor(svc)}` : `₹${svc.price.toLocaleString("en-IN")}`}
                </p>
              </div>
              <Button size="sm" onClick={() => openService(svc)} className="rounded-xl shrink-0" style={{ backgroundColor: catColor }} data-testid={`ambulance-book-${svc.id}`}>
                {cat === "emergency" ? "Dispatch" : "Book"}
              </Button>
            </Card>
          );
        })}
      </div>

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-5">
        <ShieldCheck className="h-3.5 w-3.5" /> Dispatch alerts nearby hospitals & paramedics in real time.
      </p>

      {/* Emergency SOS dialog */}
      <Dialog open={!!sosService} onOpenChange={(o) => !o && setSosService(null)}>
        <DialogContent className="rounded-3xl" data-testid="sos-dialog">
          <DialogHeader><DialogTitle>Dispatch {sosService?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Select the emergency reason to dispatch an ambulance</DialogDescription>
          <p className="text-sm text-muted-foreground -mt-1">What's the emergency?</p>
          <div className="flex flex-wrap gap-2 my-2">
            {REASONS.map((r) => (
              <button key={r} onClick={() => setReason(r)} data-testid={`sos-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${reason === r ? "bg-destructive text-white border-transparent" : "bg-card border-border text-foreground"}`}>
                {r}
              </button>
            ))}
          </div>
          <Button onClick={confirmSOS} disabled={dispatching || !reason} className="w-full h-12 rounded-2xl bg-destructive text-destructive-foreground" data-testid="sos-confirm-dispatch">
            {dispatching ? <Loader2 className="h-4 w-4 animate-spin" /> : `Dispatch Now`}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">We'll use your device's live location to send help.</p>
        </DialogContent>
      </Dialog>

      {/* Booking dialog */}
      <Dialog open={!!bookSvc} onOpenChange={(o) => !o && setBookSvc(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader><DialogTitle>Book {bookSvc?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Enter pickup and recipient details to book this service</DialogDescription>
          <div className="space-y-3">
            <div className="flex gap-2">
              {["self", "other"].map((v) => (
                <button key={v} onClick={() => setBookingFor(v)} data-testid={`booking-for-${v}`}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${bookingFor === v ? "bg-primary text-primary-foreground border-transparent" : "bg-card border-border text-muted-foreground"}`}>
                  {v === "self" ? "For myself" : "For someone else"}
                </button>
              ))}
            </div>
            {bookingFor === "other" && (
              <>
                <Input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Recipient name" className="rounded-2xl h-11" data-testid="booking-recipient-name" />
                <Input value={rPhone} onChange={(e) => setRPhone(e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="Recipient phone" className="rounded-2xl h-11" data-testid="booking-recipient-phone" />
              </>
            )}
            <Input value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="Pickup address" className="rounded-2xl h-11" data-testid="booking-pickup" />
            <Input value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder="Drop-off address (optional)" className="rounded-2xl h-11" data-testid="booking-dropoff" />
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-2xl min-h-[70px]" data-testid="booking-notes" />
            <div className="flex items-center justify-between rounded-2xl bg-muted px-4 py-3">
              <span className="text-sm font-medium">Service fee</span>
              <span className="font-bold">₹{bookSvc?.price?.toLocaleString("en-IN")}</span>
            </div>
            <Button onClick={submitBooking} disabled={submitting} className="w-full h-12 rounded-2xl" data-testid="booking-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Payment collected after service. You'll get a live tracking page next.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
